/**
 * Multi-page dashboards — the flank counterpart of flank-20's pages (FLA2-641).
 *
 * A page owns its widgets, so one dashboard can hold "DV360" and "Simpli.fi"
 * views with genuinely different content. This is the feature partners have
 * been reaching for when they report that saved views "don't save": saved views
 * only ever captured filters, and every view shared one set of widgets.
 *
 * Stored in the dashboard's existing `config` with a `pageId` on each widget,
 * so nothing here needs a ribeye-api migration. A dashboard saved before pages
 * existed has no `pages` and no `pageId` anywhere; it reads back as a single
 * page holding everything, and migrates on the next save — the same approach
 * `splitLayout` already uses for legacy filters.
 */

export interface DashboardPage {
  id: string;
  name: string;
}

const PAGES_CONFIG_KEY = 'pages';

export const DEFAULT_PAGE_ID = 'page-1';
export const DEFAULT_PAGE_NAME = 'Page 1';

export const newPageId = (): string => `page-${Math.random().toString(36).slice(2, 9)}`;

/** A widget as stored in `layout`, with the optional page it belongs to. */
export type PagedWidget = Record<string, unknown> & { id?: string; pageId?: string };

/**
 * Pages for a dashboard. Always returns at least one, so callers never handle
 * an empty tab strip.
 */
export const readPages = (config: Record<string, unknown> | null | undefined): DashboardPage[] => {
  const raw = config?.[PAGES_CONFIG_KEY];
  if (!Array.isArray(raw) || !raw.length) return [{ id: DEFAULT_PAGE_ID, name: DEFAULT_PAGE_NAME }];
  // A duplicate or empty id makes widget ownership ambiguous, and a non-string
  // name reaches the tab renderer as an invalid React child. Neither should be
  // trusted from stored config.
  const seen = new Set<string>();
  const pages: DashboardPage[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { id, name } = entry as Partial<DashboardPage>;
    if (typeof id !== 'string' || !id.trim() || seen.has(id)) continue;
    seen.add(id);
    pages.push({ id, name: typeof name === 'string' && name.trim() ? name : DEFAULT_PAGE_NAME });
  }
  return pages.length ? pages : [{ id: DEFAULT_PAGE_ID, name: DEFAULT_PAGE_NAME }];
};

/** Merge pages into a config without dropping siblings (filters, views, tenant fields). */
export const writePages = (
  config: Record<string, unknown> | null | undefined,
  pages: DashboardPage[],
): Record<string, unknown> => ({ ...(config ?? {}), [PAGES_CONFIG_KEY]: pages });

/**
 * Give every widget a page. Anything unassigned — which is every widget on a
 * dashboard built before pages — lands on the first page rather than vanishing.
 */
export const backfillWidgetPages = <T extends PagedWidget>(widgets: T[], pages: DashboardPage[]): T[] => {
  const first = pages[0]?.id ?? DEFAULT_PAGE_ID;
  const known = new Set(pages.map(p => p.id));
  return widgets.map(w => (w.pageId && known.has(w.pageId) ? w : ({ ...w, pageId: first } as T)));
};

/** The widgets on one page, in layout order. */
export const widgetsForPage = <T extends PagedWidget>(widgets: T[], pageId: string): T[] =>
  widgets.filter(w => w.pageId === pageId);

/**
 * Delete a page and say what happens to its widgets.
 *
 * Widgets move to the nearest surviving page rather than being dropped. In 2.0
 * deleting a page left its widgets in the database belonging to no page, so
 * they could not be seen or removed — two of Bonneville's went missing that
 * way. Reassigning keeps that impossible by construction.
 *
 * The last page cannot be deleted; a dashboard always has somewhere to put a
 * widget.
 */
export const removePage = <T extends PagedWidget>(
  pages: DashboardPage[],
  widgets: T[],
  pageId: string,
): { pages: DashboardPage[]; widgets: T[]; removed: boolean } => {
  if (pages.length <= 1) return { pages, widgets, removed: false };
  const index = pages.findIndex(p => p.id === pageId);
  if (index === -1) return { pages, widgets, removed: false };
  const nextPages = pages.filter(p => p.id !== pageId);
  // Prefer the page to the left, so widgets land where the user was looking.
  const target = pages[index - 1]?.id ?? nextPages[0].id;
  const nextWidgets = widgets.map(w => (w.pageId === pageId ? ({ ...w, pageId: target } as T) : w));
  return { pages: nextPages, widgets: nextWidgets, removed: true };
};

/** Rename a page, ignoring a blank name rather than leaving an unlabelled tab. */
export const renamePage = (pages: DashboardPage[], pageId: string, name: string): DashboardPage[] => {
  const trimmed = name.trim();
  if (!trimmed) return pages;
  return pages.map(p => (p.id === pageId ? { ...p, name: trimmed } : p));
};

/** Append a page, named for its position unless the caller supplies one. */
export const addPage = (pages: DashboardPage[], name?: string): DashboardPage[] => [
  ...pages,
  { id: newPageId(), name: name?.trim() || `Page ${pages.length + 1}` },
];

/**
 * Reorder a widget within its page, expressed against the full layout.
 *
 * The grid renders one page at a time, so drag indices are page-relative, while
 * `layout` holds every page's widgets in one array. Splicing a page-relative
 * index straight into `layout` would move whichever widget happened to sit at
 * that position — usually one on another page. This maps back through the
 * positions the page actually occupies and leaves every other page untouched.
 */
export const reorderWithinPage = <T extends PagedWidget>(
  widgets: T[],
  pageId: string,
  from: number,
  to: number,
): T[] => {
  const positions: number[] = [];
  widgets.forEach((w, i) => {
    if (w.pageId === pageId) positions.push(i);
  });
  if (from < 0 || to < 0 || from >= positions.length || to >= positions.length || from === to) return widgets;
  const pageItems = positions.map(i => widgets[i]);
  const [moved] = pageItems.splice(from, 1);
  pageItems.splice(to, 0, moved);
  const next = [...widgets];
  positions.forEach((pos, i) => {
    next[pos] = pageItems[i];
  });
  return next;
};
